import { useState, useEffect, useRef } from 'react';
import ModalBase from '@/components/base/ModalBase';
import type { Contest } from '@/mocks/adminContests';
import { mockWinnerNames } from '@/mocks/adminContests';

interface WinnerPickerModalProps {
  open: boolean;
  contest: Contest | null;
  onClose: () => void;
  onConfirmWinner: (contest: Contest, winnerName: string, winnerAvatar: string) => void;
}

export default function WinnerPickerModal({ open, contest, onClose, onConfirmWinner }: WinnerPickerModalProps) {
  const [phase, setPhase] = useState<'idle' | 'spinning' | 'revealed'>('idle');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [winner, setWinner] = useState<{ name: string; avatar: string } | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!open || !contest) {
      setPhase('idle');
      setCurrentIndex(0);
      setWinner(null);
      return;
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [open, contest]);

  const handleStartSpin = () => {
    if (!contest) return;
    setPhase('spinning');
    setWinner(null);

    let idx = 0;
    const totalNames = mockWinnerNames.length;

    intervalRef.current = setInterval(() => {
      idx = (idx + 1) % totalNames;
      setCurrentIndex(idx);
    }, 80);

    timeoutRef.current = setTimeout(() => {
      if (intervalRef.current) clearInterval(intervalRef.current);

      const finalIdx = Math.floor(Math.random() * totalNames);
      const chosen = mockWinnerNames[finalIdx];

      // Slow down to final winner
      let slowIdx = currentIndex;
      const slowInterval = setInterval(() => {
        slowIdx = (slowIdx + 1) % totalNames;
        setCurrentIndex(slowIdx);
        if (slowIdx === finalIdx) {
          clearInterval(slowInterval);
          setTimeout(() => {
            setWinner(chosen);
            setPhase('revealed');
          }, 400);
        }
      }, 150);
    }, 2500);
  };

  const handleConfirm = () => {
    if (!contest || !winner) return;
    onConfirmWinner(contest, winner.name, winner.avatar);
    onClose();
  };

  if (!open || !contest) return null;

  return (
    <ModalBase
      open={open}
      onClose={onClose}
      title="Elegir ganador"
      subtitle={`Sorteo para: ${contest.title}`}
      size="sm"
      headerIcon={
        <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center">
          <i className="ri-shining-line text-amber-600"></i>
        </div>
      }
      footer={
        <div className="flex items-center gap-2 w-full">
          {phase === 'idle' && (
            <>
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2.5 rounded-xl bg-background-100 text-foreground-600 font-semibold text-sm cursor-pointer hover:bg-background-200 transition-colors whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={handleStartSpin}
                disabled={contest.participants === 0}
                className={`flex-1 px-4 py-2.5 rounded-xl font-semibold text-sm cursor-pointer transition-all whitespace-nowrap ${
                  contest.participants > 0
                    ? 'bg-amber-500 text-white hover:bg-amber-600'
                    : 'bg-amber-200 text-amber-400 cursor-not-allowed'
                }`}
              >
                <i className="ri-play-circle-line mr-1"></i>
                Sortear ganador
              </button>
            </>
          )}
          {phase === 'spinning' && (
            <button disabled className="flex-1 px-4 py-2.5 rounded-xl bg-amber-100 text-amber-500 font-semibold text-sm cursor-wait whitespace-nowrap">
              <span className="inline-block w-4 h-4 mr-1 border-2 border-amber-500 border-t-transparent rounded-full animate-spin align-middle"></span>
              Sorteando…
            </button>
          )}
          {phase === 'revealed' && (
            <>
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2.5 rounded-xl bg-background-100 text-foreground-600 font-semibold text-sm cursor-pointer hover:bg-background-200 transition-colors whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirm}
                className="flex-1 px-4 py-2.5 rounded-xl bg-primary-500 text-white font-semibold text-sm cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-check-line mr-1"></i>
                Confirmar ganador
              </button>
            </>
          )}
        </div>
      }
    >
      <div className="space-y-4">
        {/* Info */}
        <div className="flex items-center gap-3 p-3 rounded-xl bg-amber-50 border border-amber-100">
          <div className="w-10 h-10 rounded-xl bg-amber-200 flex items-center justify-center shrink-0">
            <i className="ri-group-line text-amber-700"></i>
          </div>
          <div>
            <p className="text-amber-900 text-xs font-bold">{contest.participants.toLocaleString()} participantes</p>
            <p className="text-amber-700 text-[10px]">El ganador será elegido al azar entre todos</p>
          </div>
        </div>

        {/* Spinner area */}
        <div className="flex flex-col items-center py-6">
          {/* Name display */}
          <div className={`w-full max-w-xs mx-auto p-4 rounded-2xl border-2 transition-all duration-300 ${
            phase === 'revealed'
              ? 'border-amber-400 bg-amber-50 scale-105'
              : phase === 'spinning'
              ? 'border-amber-200 bg-amber-50/50'
              : 'border-background-200 bg-background-100'
          }`}>
            {phase === 'idle' && contest.participants === 0 && (
              <div className="text-center py-4">
                <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-foreground-100 flex items-center justify-center">
                  <i className="ri-user-search-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500 font-semibold text-sm">Sin participantes</p>
                <p className="text-foreground-400 text-xs mt-1">Este concurso no tiene participantes todavía</p>
              </div>
            )}

            {phase === 'idle' && contest.participants > 0 && (
              <div className="text-center py-4">
                <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-amber-100 flex items-center justify-center">
                  <i className="ri-shining-line text-3xl text-amber-500"></i>
                </div>
                <p className="text-foreground-700 font-bold text-sm">¿Listo para el sorteo?</p>
                <p className="text-foreground-500 text-xs mt-1">
                  Se elegirá un ganador al azar entre {contest.participants.toLocaleString()} participantes
                </p>
                <p className="text-foreground-400 text-[10px] mt-2">El concurso se finalizará automáticamente</p>
              </div>
            )}

            {(phase === 'spinning' || phase === 'revealed') && (
              <div className="text-center">
                {/* Avatar */}
                <div className={`w-20 h-20 mx-auto rounded-full overflow-hidden border-4 transition-all duration-500 ${
                  phase === 'revealed'
                    ? 'border-amber-400 shadow-lg shadow-amber-200 scale-110'
                    : 'border-amber-200'
                }`}>
                  {winner && phase === 'revealed' ? (
                    <img src={winner.avatar} alt={winner.name} className="w-full h-full object-cover" />
                  ) : (
                    <img
                      src={mockWinnerNames[currentIndex].avatar}
                      alt={mockWinnerNames[currentIndex].name}
                      className={`w-full h-full object-cover ${phase === 'spinning' ? 'animate-pulse' : ''}`}
                    />
                  )}
                </div>

                {/* Name */}
                <div className="mt-3">
                  <p className={`text-lg font-bold transition-all duration-300 ${
                    phase === 'revealed'
                      ? 'text-amber-800'
                      : 'text-foreground-700'
                  }`}>
                    {phase === 'revealed' && winner
                      ? winner.name
                      : mockWinnerNames[currentIndex].name
                    }
                  </p>
                  {phase === 'revealed' && (
                    <div className="inline-flex items-center gap-1 px-3 py-1 mt-2 rounded-full bg-amber-200 text-amber-800 text-[10px] font-bold animate-bounce">
                      <i className="ri-trophy-fill text-[10px]"></i>
                      ¡GANADOR!
                    </div>
                  )}
                  {phase === 'spinning' && (
                    <p className="text-amber-500 text-[10px] mt-1 animate-pulse">Eligiendo ganador…</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Stats after reveal */}
          {phase === 'revealed' && winner && (
            <div className="flex items-center gap-6 mt-4 pt-4 border-t border-background-200 w-full max-w-xs">
              <div className="text-center flex-1">
                <p className="text-foreground-950 font-bold text-base">{contest.participants.toLocaleString()}</p>
                <p className="text-foreground-400 text-[10px]">Participantes</p>
              </div>
              <div className="text-center flex-1">
                <p className="text-foreground-950 font-bold text-base">
                  {((1 / contest.participants) * 100).toFixed(2)}%
                </p>
                <p className="text-foreground-400 text-[10px]">Probabilidad</p>
              </div>
              <div className="text-center flex-1">
                <p className="text-foreground-950 font-bold text-base">
                  {new Date(contest.endDate).toLocaleDateString('es-MX', { month: 'short', day: 'numeric' })}
                </p>
                <p className="text-foreground-400 text-[10px]">Finalizó</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </ModalBase>
  );
}